import { cancelLiveChatInput, listLiveChatInputs, sendLiveChatInput, promoteLiveChatInput, editLiveChatInput,
  recallLiveChatInput, isRecalledInput, downloadLiveChatInputAttachment } from '../../shared/api/chatLiveInputs';
import { cancelLiveChat } from '../../shared/api/chatLive';
import { chatComposerDraftStore, chatDraftKey } from './chatComposerDraft';
import { canEditInput } from './liveChatInputs';

const EMPTY = Object.freeze({ inputs: [], capabilities: {}, edits: {}, recall: null, pending: false, loading: false,
  loaded: false, error: '', notice: '', activeRunId: null });
const storageKey = (owner, cid) => `noesa.chat.input-run.v1.${encodeURIComponent(owner)}.${cid}`;

export function createLiveChatInputStore({ list = listLiveChatInputs, send = sendLiveChatInput, remove = cancelLiveChatInput,
  promote = promoteLiveChatInput, stop = cancelLiveChat, edit = editLiveChatInput,
  recall: recallInput = recallLiveChatInput, download = downloadLiveChatInputAttachment,
  drafts = chatComposerDraftStore, storage = () => localStorage, uuid = () => crypto.randomUUID() } = {}) {
  let owner = '', generation = 0;
  const entries = new Map(), listeners = new Set();
  const notify = () => listeners.forEach(listener => listener());
  const owns = entry => entry.owner === owner && entry.generation === generation && entries.get(entry.cid) === entry;
  const publish = (entry, patch) => {
    if (!owns(entry)) return;
    entry.snapshot = { ...entry.snapshot, ...patch }; notify();
  };
  const persist = entry => {
    try {
      storage().setItem(storageKey(entry.owner, entry.cid), JSON.stringify({ runId: entry.runId,
        submission: entry.submission, edits: entry.snapshot.edits, recall: entry.snapshot.recall }));
      return true;
    } catch { return false; }
  };
  const get = (account, conversationId) => {
    const cid = Number(conversationId);
    if (!account || owner !== account || !(cid > 0)) return null;
    if (!entries.has(cid)) {
      let saved = {};
      try { saved = JSON.parse(storage().getItem(storageKey(account, cid)) || '{}'); } catch {}
      entries.set(cid, { owner, generation, cid, runId: saved.runId || '', submission: saved.submission || null,
        snapshot: { ...EMPTY, runId: saved.runId || '', edits: saved.edits || {},
          recall: saved.recall ? { ...saved.recall, phase: saved.recall.phase === 'requesting' ? 'unconfirmed' : saved.recall.phase } : null,
          inputs: [saved.recall?.receipt || saved.recall?.input,
            ...Object.values(saved.edits || {}).map(edit => edit.recall?.receipt || edit.recall?.input)]
            .filter((input, index, inputs) => input && inputs.findIndex(item => item?.id === input.id) === index) }, sequence: 0 });
    }
    return entries.get(cid);
  };
  const confirmSubmission = async (entry, receipt) => {
    if (!owns(entry) || !entry.submission || receipt.request_id !== entry.submission.payload.request_id) return;
    const submission = entry.submission, submitted = submission.draft;
    const current = () => owns(entry) && entry.submission === submission;
    try {
      if (submitted && !await drafts.consumeConfirmed(submitted, current)) return;
    } catch { publish(entry, { error: 'media_read' }); return; }
    if (!current()) return;
    entry.submission = null; persist(entry);
  };
  const recordReceipt = async (entry, receipt) => {
    if (!owns(entry)) return;
    const inputs = entry.snapshot.inputs;
    publish(entry, { inputs: inputs.some(input => input.id === receipt.id)
      ? inputs.map(input => input.id === receipt.id ? receipt : input) : [...inputs, receipt] });
    await confirmSubmission(entry, receipt);
  };
  const updateEdit = (entry, id, value) => {
    if (!owns(entry)) return;
    const edits = { ...entry.snapshot.edits };
    if (value) edits[id] = value; else delete edits[id];
    publish(entry, { edits });
    persist(entry);
  };
  const setRecall = (entry, patch) => {
    publish(entry, { recall: patch === null ? null : { ...entry.snapshot.recall, ...patch } });
    return persist(entry);
  };
  const hasRecall = (entry, operation) => owns(entry) && entry.snapshot.recall?.restoreId === operation.restoreId;
  const prepareRecallFiles = async (entry, operation) => {
    if (!operation.attachments.length) return [];
    let cached = await drafts.readRecovery(operation.draftKey, operation.restoreId);
    if (!hasRecall(entry, operation)) throw new Error('Owner changed');
    if (cached.length === operation.attachments.length && cached.every((file, index) =>
      file.name === operation.attachments[index].name && file.size === operation.attachments[index].size)) return cached;
    cached = [];
    for (const attachment of operation.attachments) {
      if (!hasRecall(entry, operation)) throw new Error('Owner changed');
      cached.push(await download(operation.runId, operation.receipt || operation.input, attachment));
    }
    if (!hasRecall(entry, operation)) throw new Error('Owner changed');
    await drafts.stageRecovery(operation.draftKey, operation.restoreId, cached);
    return cached;
  };
  const restoreRecall = async (entry, operation, preparedFiles) => {
    const current = () => hasRecall(entry, operation);
    if (!current()) return false;
    setRecall(entry, { phase: 'restoring', error: '' });
    try {
      const alreadyRestored = await drafts.isRestored(operation.draftKey, operation.restoreId, operation.attachments.length > 0);
      if (!current()) return false;
      const attachments = alreadyRestored ? [] : preparedFiles || await prepareRecallFiles(entry, operation);
      if (!current()) return false;
      const restored = alreadyRestored || await drafts.restore(operation.draftKey, { id: operation.restoreId, text: operation.text, attachments }, current);
      if (!current()) return false;
      if (!restored) throw new Error('Draft restoration was not persisted');
      const local = entry.snapshot.edits[operation.inputId];
      if (local && local.text === operation.text && local.version === operation.editVersion) updateEdit(entry, operation.inputId, null);
      entry.restoredRecallId = operation.restoreId;
      setRecall(entry, null);
      await drafts.clearRecovery(operation.draftKey, operation.restoreId);
      return owns(entry);
    } catch {
      if (current()) setRecall(entry, { phase: 'restore_failed', error: 'recall_restore_failed' });
      return false;
    }
  };
  const store = {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    setOwner(account) {
      const next = String(account || '');
      if (next === owner) return;
      owner = next; generation += 1; entries.clear(); notify();
    },
    getSnapshot(account, cid) { return get(account, cid)?.snapshot || EMPTY; },
    reportUnavailable(account, cid) {
      const entry = get(account, cid);
      if (entry) publish(entry, { error: 'unavailable', notice: '' });
    },
    reportWaiting(account, cid) {
      const entry = get(account, cid);
      if (entry) publish(entry, { notice: 'loading', error: '' });
    },
    registerRun(account, cid, runId) {
      const previous = get(account, cid);
      if (!previous || !runId || previous.runId === runId || previous.snapshot.activeRunId === runId
        || previous.snapshot.inputs.some(input => input.execution_run_id === runId)) return;
      const entry = { ...previous, runId, submission: null, sequence: 0 };
      entries.set(entry.cid, entry);
      publish(entry, { ...EMPTY, edits: previous.snapshot.edits, recall: previous.snapshot.recall,
        inputs: previous.snapshot.recall ? [previous.snapshot.recall.receipt || previous.snapshot.recall.input] : [], runId, activeRunId: runId });
      persist(entry);
    },
    async refresh(account, cid, { recover = false } = {}) {
      const entry = get(account, cid);
      if (!entry?.runId || entry.snapshot.pending) return;
      const sequence = ++entry.sequence, runId = entry.runId;
      if (!entry.snapshot.loaded) publish(entry, { loading: true });
      try {
        let result = await list(runId);
        if (!owns(entry) || sequence !== entry.sequence) return;
        if (result.active_run_id && result.active_run_id !== runId) {
          const activeId = result.active_run_id;
          const active = await list(activeId);
          if (!owns(entry) || sequence !== entry.sequence) return;
          result = { ...active, capabilities: active.active_run_id === activeId ? active.capabilities : {} };
        }
        const recovery = entry.snapshot.recall;
        if (recovery && !result.inputs.some(input => String(input.id) === String(recovery.inputId)) && recovery.runId !== runId) {
          const origin = await list(recovery.runId);
          const input = origin.inputs.find(item => String(item.id) === String(recovery.inputId));
          if (input) result = { ...result, inputs: [...result.inputs, input] };
        }
        if (!owns(entry) || sequence !== entry.sequence) return;
        const recoveries = [entry.snapshot.recall, ...Object.values(entry.snapshot.edits).map(edit => edit.recall)].filter(Boolean);
        for (const operation of recoveries) {
          if (!result.inputs.some(input => String(input.id) === String(operation.inputId))) {
            result = { ...result, inputs: [...result.inputs, operation.receipt || operation.input] };
          }
        }
        publish(entry, { inputs: result.inputs, capabilities: result.capabilities || {}, activeRunId: result.active_run_id,
          queueRootId: result.queue_root_id,
          notice: entry.snapshot.notice === 'loading' ? '' : entry.snapshot.notice,
          error: entry.snapshot.error === 'offline' || (entry.snapshot.error === 'unavailable' && result.capabilities?.queue)
            ? '' : entry.snapshot.error, loading: false, loaded: true });
        for (const input of result.inputs) await confirmSubmission(entry, input);
        if (!owns(entry) || entry.snapshot.pending) return;
        const operation = entry.snapshot.recall;
        const receipt = operation && result.inputs.find(input => isRecalledInput(input, operation.inputId, operation.expectedRevision));
        if (receipt) setRecall(entry, { receipt });
        const canRetryRestore = operation && (recover || !['restore_failed', 'files_failed'].includes(operation.phase));
        if (canRetryRestore && (receipt || operation.kind === 'recover_edit' || operation.receipt)) {
          publish(entry, { pending: true });
          try { await restoreRecall(entry, entry.snapshot.recall); }
          finally { publish(entry, { pending: false }); }
        }
      } catch (error) {
        if (sequence === entry.sequence) publish(entry, { loading: false, notice: entry.snapshot.notice === 'loading' ? '' : entry.snapshot.notice, error: 'offline' });
      }
    },
    async submit(account, cid, draft, mode = 'queue') {
      const entry = get(account, cid);
      if (!entry || entry.snapshot.pending || entry.snapshot.recall) return false;
      if (entry.runId && !entry.snapshot.loaded) {
        store.reportWaiting(account, cid);
        await store.refresh(account, cid);
        return false;
      }
      const capabilities = entry.snapshot.capabilities;
      const text = String(draft.text || '');
      const files = draft.attachments || [];
      const notice = files.length && (mode !== 'queue' || capabilities.attachments !== true) ? 'media' : [...text].length > 24000 ? 'length'
        : !entry.runId || !capabilities.queue || (mode === 'steer' && (!entry.snapshot.activeRunId || (!capabilities.native_steering && !capabilities.step_boundary))) ? 'unavailable' : '';
      if (notice) { publish(entry, notice === 'unavailable' ? { error: notice, notice: '' } : { notice }); return false; }
      if ((!text.trim() && !files.length) || !draft.ready || draft.key !== chatDraftKey(account, cid)) return false;
      entry.sequence += 1;
      publish(entry, { pending: true, notice: '', error: '', loading: false });
      let prepared = false;
      try {
        const submitted = await drafts.captureSubmission(draft);
        if (!owns(entry)) return false;
        const identity = JSON.stringify(submitted.files.map(file => file.fingerprint));
        const old = entry.submission;
        const payload = old?.payload.text === text && old?.payload.mode === mode && (old.fileIdentity || '[]') === identity
          ? old.payload : { request_id: uuid(), text, mode };
        const targetRun = old?.payload === payload ? old.runId : (entry.snapshot.activeRunId || entry.runId);
        // File bytes remain in IndexedDB; only content hashes and selection IDs enter this journal.
        entry.submission = { payload, runId: targetRun, draft: submitted, fileIdentity: identity };
        if (!persist(entry)) throw new Error('Cannot persist submission identity');
        prepared = true;
        const receipt = await (files.length ? send(targetRun, payload, files) : send(targetRun, payload));
        if (!owns(entry)) return false;
        await recordReceipt(entry, receipt);
        return true;
      } catch (error) {
        publish(entry, { error: !prepared ? 'media_read' : error?.status === 409 ? 'conflict' : 'failed' });
        return false;
      } finally {
        if (owns(entry)) {
          publish(entry, { pending: false });
          await store.refresh(account, cid);
        }
      }
    },
    async remove(account, cid, input) { return store.change(account, cid, input, 'remove'); },
    async recall(account, cid, input, draft) { return store.startRecall(account, cid, input, draft, 'recall'); },
    async recoverEdit(account, cid, input, draft) { return store.startRecall(account, cid, input, draft, 'recover_edit'); },
    async recheckRecall(account, cid) {
      const entry = get(account, cid), operation = entry?.snapshot.recall;
      if (!operation || entry.snapshot.pending) return false;
      await store.refresh(account, cid, { recover: true });
      return owns(entry) && entry.restoredRecallId === operation.restoreId;
    },
    dismissRecall(account, cid) {
      const entry = get(account, cid), operation = entry?.snapshot.recall;
      if (!operation || entry.snapshot.pending) return false;
      const local = entry.snapshot.edits[operation.inputId];
      const version = local?.version ?? operation.editVersion ?? 0;
      updateEdit(entry, operation.inputId, { ...local, text: local?.text ?? operation.text,
        expectedRevision: local?.expectedRevision ?? operation.expectedRevision, version,
        error: operation.error, recall: { ...operation, editVersion: version } });
      if (!persist(entry)) return false;
      setRecall(entry, null);
      return true;
    },
    async startRecall(account, cid, input, draft, kind) {
      const entry = get(account, cid);
      if (!entry || entry.snapshot.pending || draft?.key !== chatDraftKey(account, cid)) return false;
      let existing = entry.snapshot.recall;
      const local = entry.snapshot.edits[input.id];
      if (!existing && local?.recall) existing = local.recall;
      const current = entry.snapshot.inputs.find(item => String(item.id) === String(input.id)) || existing?.receipt || existing?.input;
      if (existing && String(existing.inputId) !== String(input.id)) return false;
      if (!existing && !current) return false;
      if (kind === 'recall' && !existing && !canEditInput(current)) {
        return local ? store.recoverEdit(account, cid, input, draft) : false;
      }
      if (kind === 'recover_edit' && (canEditInput(current) || (!local && !existing))) return false;
      const operation = existing && kind === existing.kind ? existing : {
        inputId: current.id, runId: current.run_id || entry.runId, expectedRevision: current.revision,
        draftKey: draft.key, text: local?.text ?? existing?.text ?? current.text,
        editVersion: local?.version, attachments: current.attachments || [], input: current, kind,
        restoreId: kind === 'recall' ? `recall:${current.run_id || entry.runId}:${current.id}:${current.revision}` : `recover-edit:${uuid()}`,
        phase: 'preparing', error: '',
      };
      if (!setRecall(entry, operation)) {
        publish(entry, { recall: { ...operation, phase: 'restore_failed', error: 'recall_storage_failed' } });
        return false;
      }
      entry.sequence += 1;
      publish(entry, { pending: true, loading: false, error: '', notice: '' });
      let restored = false, prepared = false;
      try {
        if (operation.receipt || operation.kind === 'recover_edit'
          || await drafts.isRestored(operation.draftKey, operation.restoreId, operation.attachments.length > 0)) {
          return await restoreRecall(entry, operation);
        }
        const files = await prepareRecallFiles(entry, operation);
        if (!hasRecall(entry, operation)) return false;
        prepared = true;
        if (operation.kind === 'recall' && !operation.receipt) {
          if (!setRecall(entry, { phase: 'requesting', error: '' })) throw new Error('Cannot persist recall intent');
          const receipt = await recallInput(operation.runId, operation.inputId, { expected_revision: operation.expectedRevision });
          if (!hasRecall(entry, operation)) return false;
          if (!isRecalledInput(receipt, operation.inputId, operation.expectedRevision)) throw new Error('Unconfirmed recall');
          await recordReceipt(entry, receipt);
          if (!hasRecall(entry, operation)) return false;
          setRecall(entry, { receipt });
        }
        restored = await restoreRecall(entry, entry.snapshot.recall, files);
      } catch (error) {
        if (hasRecall(entry, operation)) {
          const conflict = error?.status === 409;
          const code = error?.code || error?.detail?.code;
          if (conflict) {
            const errorCode = code === 'input_revision_conflict' ? 'recall_conflict' : 'recall_dispatched';
            const localEdit = entry.snapshot.edits[operation.inputId];
            if (localEdit?.recall) updateEdit(entry, operation.inputId, { ...localEdit, recall: undefined, error: errorCode });
            setRecall(entry, null);
            publish(entry, { error: errorCode });
            await drafts.clearRecovery(operation.draftKey, operation.restoreId);
          } else setRecall(entry, { phase: !prepared ? 'files_failed' : 'unconfirmed',
            error: !prepared ? 'recall_files_failed' : 'recall_unconfirmed' });
        }
      } finally {
        if (owns(entry)) { publish(entry, { pending: false }); await store.refresh(account, cid); }
      }
      return owns(entry) && (restored || entry.restoredRecallId === operation.restoreId);
    },
    beginEdit(account, cid, input) {
      const entry = get(account, cid);
      const current = entry?.snapshot.inputs.find(item => item.id === input.id);
      if (!entry || entry.snapshot.pending || entry.snapshot.recall || !canEditInput(current) || entry.snapshot.edits[input.id]) return;
      updateEdit(entry, input.id, { text: current.text, expectedRevision: current.revision, version: 0, error: '' });
    },
    changeEdit(account, cid, input, text) {
      const entry = get(account, cid), current = entry?.snapshot.edits[input.id];
      if (!current || !canEditInput(entry.snapshot.inputs.find(item => item.id === input.id))) return;
      updateEdit(entry, input.id, { ...current, text, version: current.version + 1, error: current.error === 'edit_empty' || current.error === 'length' ? '' : current.error });
    },
    cancelEdit(account, cid, input) {
      const entry = get(account, cid);
      if (entry && entry.snapshot.savingInputId !== input.id) updateEdit(entry, input.id, null);
    },
    rebaseEdit(account, cid, input) {
      const entry = get(account, cid), current = entry?.snapshot.edits[input.id];
      const latest = entry?.snapshot.inputs.find(item => item.id === input.id);
      if (!current || entry.snapshot.pending || !canEditInput(latest)) return;
      updateEdit(entry, input.id, { ...current, expectedRevision: latest.revision, version: current.version + 1, error: '' });
    },
    async saveEdit(account, cid, input) {
      const entry = get(account, cid), submitted = entry?.snapshot.edits[input.id];
      if (!entry || !submitted || entry.snapshot.pending || entry.snapshot.recall) return false;
      const current = entry.snapshot.inputs.find(item => item.id === input.id);
      const error = !canEditInput(current) || submitted.error === 'edit_dispatched' ? 'edit_dispatched' : !submitted.text.trim() ? 'edit_empty'
        : [...submitted.text].length > 24000 ? 'length'
          : current.revision !== submitted.expectedRevision && current.text !== submitted.text ? 'edit_conflict' : '';
      if (error) { updateEdit(entry, input.id, { ...submitted, error }); return false; }
      entry.sequence += 1;
      publish(entry, { pending: true, savingInputId: input.id, loading: false });
      updateEdit(entry, input.id, { ...submitted, error: '' });
      try {
        const receipt = await edit(current.run_id || entry.runId, input.id, { text: submitted.text, expected_revision: submitted.expectedRevision });
        if (!owns(entry)) return false;
        await recordReceipt(entry, receipt);
        if (!owns(entry)) return false;
        const latest = entry.snapshot.edits[input.id];
        if (latest?.version === submitted.version) updateEdit(entry, input.id, null);
        else if (latest) updateEdit(entry, input.id, { ...latest, expectedRevision: receipt.revision, error: '' });
        return true;
      } catch (error) {
        const latest = entry.snapshot.edits[input.id];
        const code = error?.code || error?.detail?.code;
        if (latest) updateEdit(entry, input.id, { ...latest, error: error?.status === 409
          ? (code === 'input_not_editable' ? 'edit_dispatched' : 'edit_conflict') : 'edit_failed' });
        return false;
      } finally {
        if (owns(entry)) { publish(entry, { pending: false, savingInputId: null }); await store.refresh(account, cid); }
      }
    },
    async promote(account, cid, input) { return store.change(account, cid, input, 'promote'); },
    async change(account, cid, input, action) {
      const entry = get(account, cid);
      if (!entry || entry.snapshot.pending || entry.snapshot.recall || !['queued', 'paused'].includes(input.status)) return;
      if (action === 'promote' && (input.status !== 'queued' || input.mode !== 'queue' || input.attachments?.length)) return;
      entry.sequence += 1;
      publish(entry, { pending: true, error: '', loading: false });
      try { await recordReceipt(entry, await (action === 'promote' ? promote : remove)(input.run_id || entry.runId, input.id)); }
      catch (error) { publish(entry, { error: error?.status === 409 ? 'conflict' : 'failed' }); }
      finally {
        if (owns(entry)) { publish(entry, { pending: false }); await store.refresh(account, cid); }
      }
    },
    async stop(account, cid) {
      const entry = get(account, cid);
      if (!entry?.runId || entry.snapshot.stopping) return;
      publish(entry, { stopping: true });
      try { await stop(entry.runId); }
      catch { publish(entry, { error: 'failed' }); }
      finally { if (owns(entry)) { publish(entry, { stopping: false }); await store.refresh(account, cid); } }
    },
  };
  return store;
}

export const liveChatInputStore = createLiveChatInputStore();
